import { useRef } from "react";
import { AnimatePresence } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Clock, AlertCircle, CreditCard } from "lucide-react";
import BannerContent, { type BannerConfig } from "./Content";
import { useSubscriptionAccess } from "@/hooks/useSubscriptionAccess";
import useAuth from "@/hooks/useAuth";
import { useFeatureFlagsContext } from "@/contexts/FeatureFlagsContext";
import { FeatureFlagKey } from "@/types/featureFlags";
import { SubscriptionStatus } from "@/types/subscription";
import { DateUtil } from "@/utils";
import { ROUTES_PATH } from "@/router";

export default function AccessStatus() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { initialAuthChecked } = useAuth();
  const { isEnabled } = useFeatureFlagsContext();
  const paywallEnabled = isEnabled(FeatureFlagKey.SUBSCRIPTION_PAYWALL);
  const {
    hasAccess,
    daysRemaining,
    status,
    isGracePeriod,
    accessEndsAt,
    cancelAtPeriodEnd,
  } = useSubscriptionAccess();
  let config: BannerConfig | null = null;

  // Detectar al montar si venimos de checkout — ref para no verse afectado
  // por la limpieza de la URL que hace SubscriptionProtected vía useEffect.
  const cameFromCheckout = useRef(searchParams.get("session_id") !== null);

  // No mostrar nada si tiene acceso completo (>30 días restantes) y estado activo
  const isFullAccess =
    hasAccess &&
    daysRemaining !== null &&
    daysRemaining > 30 &&
    status === SubscriptionStatus.ACTIVE;
  // Suprimir banners negativos mientras SubscriptionProtected verifica el pago.
  // Evita mostrar "suscripción expirada" junto con el loading de verificación.
  const isVerifyingPayment = cameFromCheckout.current && !hasAccess;

  // No renderizar banner hasta tener datos frescos del servidor
  // Si el paywall está desactivado, los banners de suscripción no aplican
  if (initialAuthChecked && !isFullAccess && !isVerifyingPayment && paywallEnabled) {
    // Trial activo (últimos 3 días)
    if (
      status === SubscriptionStatus.TRIAL_ACTIVE &&
      daysRemaining !== null &&
      daysRemaining <= 7
    ) {
      const daysLabel =
        daysRemaining === 1 ? "1 día" : `${Math.max(0, daysRemaining)} días`;
      config = {
        alertClassName: "border-amber-500 bg-amber-50 mt-4",
        Icon: Clock,
        iconClassName: "stroke-amber-600",
        titleClassName: "text-amber-800",
        title: "Tu prueba gratuita expira pronto",
        descriptionClassName: "text-amber-700",
        description: (
          <>
            Te queda <strong>{daysLabel}</strong> de acceso completo. Actualiza
            ahora para no perder el acceso.
          </>
        ),
        button: {
          label: "Ver planes",
          onClick: () => navigate(ROUTES_PATH.PRICING),
          className: "bg-amber-600 hover:bg-amber-700 text-white border-none",
        },
      };
    }

    // Trial expirado
    else if (status === SubscriptionStatus.TRIAL_EXPIRED) {
      config = {
        alertClassName: "border-red-600 bg-red-50 mt-4",
        Icon: AlertCircle,
        iconClassName: "stroke-red-600",
        titleClassName: "text-red-700",
        title: "Tu prueba gratuita ha expirado",
        descriptionClassName: "text-red-600",
        description: "Para continuar usando StartFly, selecciona un plan.",
        button: {
          label: "Activar Subscripción",
          onClick: () => navigate(ROUTES_PATH.PRICING),
          variant: "destructive",
        },
      };
    }

    // Usuario legacy con grace period (últimos 14 días)
    else if (
      status === SubscriptionStatus.LEGACY_GRACE_PERIOD &&
      daysRemaining !== null &&
      daysRemaining <= 14
    ) {
      config = {
        alertClassName: "border-amber-500 bg-amber-50 mt-4",
        Icon: Clock,
        iconClassName: "stroke-amber-700",
        titleClassName: "text-amber-800",
        title: "Tu acceso gratuito expira pronto",
        descriptionClassName: "text-amber-700",
        description: (
          <>
            Te quedan <strong>{Math.max(0, daysRemaining)} días</strong> de
            acceso gratuito. Suscríbete ahora para mantener todas las
            funcionalidades.
          </>
        ),
        button: {
          label: "Ver Planes",
          onClick: () => navigate(ROUTES_PATH.PRICING),
          className: "bg-amber-600 hover:bg-amber-700 text-white border-none",
        },
      };
    }

    // Suscripción cancelada pero aún dentro del período pagado (acceso temporal)
    else if (status === SubscriptionStatus.CANCELED && hasAccess && accessEndsAt) {
      config = {
        alertClassName: "border-orange-500 bg-orange-50 mt-4",
        Icon: AlertCircle,
        iconClassName: "stroke-orange-700",
        titleClassName: "text-orange-800",
        title: "Tu suscripción ha sido cancelada",
        descriptionClassName: "text-orange-700",
        description: (
          <>
            Mantendrás acceso hasta el{" "}
            <strong>{DateUtil.formatDateToLocaleString(accessEndsAt)}</strong>
            {daysRemaining !== null && daysRemaining > 0 && (
              <> ({daysRemaining} días restantes)</>
            )}
            . Después de esa fecha, perderás el acceso.
          </>
        ),
        button: {
          label: "Reactivar Suscripción",
          onClick: () => navigate(ROUTES_PATH.PRICING),
          className: "bg-orange-600 hover:bg-orange-700 text-white border-none",
        },
      };
    }

    // Suscripción cancelada y período pagado ya expiró (sin acceso)
    else if (status === SubscriptionStatus.CANCELED && !hasAccess) {
      config = {
        alertClassName: "border-red-600 bg-red-50 mt-4",
        Icon: AlertCircle,
        iconClassName: "stroke-red-600",
        titleClassName: "text-red-700",
        title: "Tu suscripción ha expirado",
        descriptionClassName: "text-red-600",
        description:
          "Tu suscripción fue cancelada y el período pagado ha finalizado. Suscríbete de nuevo para recuperar el acceso.",
        button: {
          label: "Ver Planes",
          onClick: () => navigate(ROUTES_PATH.PRICING),
          variant: "destructive",
        },
      };
    }

    // Legacy expirado o acceso vencido sin suscripción
    else if (status === SubscriptionStatus.EXPIRED) {
      config = {
        alertClassName: "border-red-600 bg-red-50 mt-4",
        Icon: AlertCircle,
        iconClassName: "stroke-red-600",
        titleClassName: "text-red-700",
        title: "Tu acceso gratuito ha vencido",
        descriptionClassName: "text-red-600",
        description:
          "Tu período de acceso gratuito ha finalizado. Suscríbete para continuar usando StartFly.",
        button: {
          label: "Ver Planes",
          onClick: () => navigate(ROUTES_PATH.PRICING),
          variant: "destructive",
        },
      };
    }

    // Past due fuera del grace period (acceso suspendido)
    else if (status === SubscriptionStatus.UNPAID) {
      config = {
        alertClassName: "border-red-600 bg-red-50 mt-4",
        Icon: CreditCard,
        iconClassName: "stroke-red-600",
        titleClassName: "text-red-800",
        title: "Tu acceso ha sido suspendido",
        descriptionClassName: "text-red-700",
        description:
          "Tu último pago falló y el período de gracia ha vencido. Actualiza tu método de pago para recuperar el acceso.",
        button: {
          label: "Actualizar Método de Pago",
          onClick: () => navigate(ROUTES_PATH.BILLING),
          variant: "destructive",
        },
      };
    }

    // Past due (periodo de gracia)
    else if (isGracePeriod) {
      config = {
        alertClassName: "border-red-500 bg-red-50 mt-4",
        Icon: CreditCard,
        iconClassName: "stroke-red-600",
        titleClassName: "text-red-800",
        title: "Problema con tu pago",
        descriptionClassName: "text-red-700",
        description: (
          <>
            Tu último pago falló. Actualiza tu método de pago antes del{" "}
            {DateUtil.formatDateToLocaleString(accessEndsAt)} para evitar perder
            acceso.
          </>
        ),
        button: {
          label: "Actualizar Método de Pago",
          onClick: () => navigate(ROUTES_PATH.BILLING),
          variant: "destructive",
        },
      };
    }

    // Suscripción cancelada pero aún activa (cancel_at_period_end = true)
    else if (
      status === SubscriptionStatus.ACTIVE &&
      cancelAtPeriodEnd &&
      accessEndsAt
    ) {
      config = {
        alertClassName: "border-orange-500 bg-orange-50 mt-4",
        Icon: AlertCircle,
        iconClassName: "stroke-orange-700",
        titleClassName: "text-orange-800",
        title: "Tu suscripción ha sido cancelada",
        descriptionClassName: "text-orange-700",
        description: (
          <>
            Mantendrás acceso hasta el{" "}
            <strong>{DateUtil.formatDateToLocaleString(accessEndsAt)}</strong>
            {daysRemaining !== null && daysRemaining > 0 && (
              <> ({daysRemaining} días restantes)</>
            )}
            . Después de esa fecha, perderás el acceso.
          </>
        ),
        button: {
          label: "Reactivar Suscripción",
          onClick: () => navigate(ROUTES_PATH.PRICING),
          className: "bg-orange-600 hover:bg-orange-700 text-white border-none",
        },
      };
    }

    // Subscripción activa próxima a vencer (<7 días) - renovación automática
    else if (
      status === SubscriptionStatus.ACTIVE &&
      !cancelAtPeriodEnd &&
      daysRemaining !== null &&
      daysRemaining <= 7
    ) {
      config = {
        alertClassName: "border-blue-400 bg-blue-50 mt-4",
        Icon: Clock,
        iconClassName: "stroke-blue-600",
        titleClassName: "text-blue-800",
        title: "Tu subscripción se renueva pronto",
        descriptionClassName: "text-blue-700",
        description: (
          <>
            Se renovará automáticamente el{" "}
            {DateUtil.formatDateToLocaleString(accessEndsAt)}.
          </>
        ),
      };
    }
  }

  return (
    <AnimatePresence>
      {config && <BannerContent key="status-banner" {...config} />}
    </AnimatePresence>
  );
}
